import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective } from '@angular/forms';
import { ComboboxComponent, provideValueAccessor } from 'forms-ui';
import { ProductCategoryEntity as ApiProductCategory, ProductCategoryComponentStore, ProductCategoryStore } from 'products-data';

// Extended category type with level and thumbnail
type ProductCategory = ApiProductCategory & { thumbnailUrl: string; level: number };

/**
 * Product Category Combobox Component
 *
 * Wraps the generic ComboboxComponent to provide category-specific functionality.
 *
 * Form Value Behavior:
 * - When user selects a category from dropdown: emits category.id (string)
 * - When user types text: emits the typed text (string)
 * - When cleared: emits null
 *
 * This allows compatibility with TextFormControl while still providing rich selection UX.
 * Use selectedItem model if you need access to the full ProductCategory object.
 */
@Component({
  selector: 'ideal-product-category-combobox',
  imports: [ComboboxComponent],
  templateUrl: './product-category-combobox.component.html',
  styleUrl: './product-category-combobox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductCategoryComponentStore, provideValueAccessor(ProductCategoryComboboxComponent, false)],
  host: {
    '[class.has-input]': 'hasInputValue()',
  },
})
export class ProductCategoryComboboxComponent implements ControlValueAccessor, AfterViewInit {
  private readonly _categoryStore = inject(ProductCategoryComponentStore);
  private readonly _formGroup = inject(FormGroupDirective, { optional: true });
  private readonly _categories = this._categoryStore.categories;
  // Inputs
  public readonly label = input<string>('Category');
  public readonly placeholder = input<string>('Type or select a category...');
  public readonly name = input<string>('');

  // Models/Outputs
  public readonly selectedItem = model<ProductCategory | null>(null);
  public readonly selectedIndex = model<number>(-1);

  protected readonly inputValue = signal<unknown>(undefined);
  protected readonly matchedCategoryIndices = signal<number[]>([]);

  // Track matched indices and parent IDs
  protected readonly matchedCategories = computed(() => {
    const categories = this.choices();
    const indices = this.matchedCategoryIndices();
    return indices.map((idx) => categories[idx]);
  });
  protected readonly parentIdsWithMatches = computed(() => {
    const matches = this.matchedCategories();
    const parentIds = new Set<string>();
    for (const category of matches) {
      const parts = category.id.split('.');
      for (let i = 1; i < parts.length; i++) {
        const parentId = parts.slice(0, i).join('.');
        parentIds.add(parentId);
      }
    }
    return parentIds;
  });

  protected readonly hasInputValue = computed(() => {
    return !!this.selectedItem() || !!this.inputValue();
  });
  // Store data
  protected readonly choices = computed(
    () => this._categories().map((cat) => ({ ...cat, level: cat.id.split('.').length - 1 })) as ProductCategory[],
  );
  protected readonly loading = computed(() => this._categoryStore.categories.loading());

  // Reference to child combobox
  protected readonly comboboxRef = viewChild.required<ComboboxComponent<ProductCategory>>('comboboxRef');

  // Label function for displaying category names
  protected readonly labelFn = (category: ProductCategory) => category.name;

  // Handle matched indices from combobox
  // protected onMatchedIndices(indices: number[]): void {
  //   const categories = this.choices();
  //   const matchedIds = new Set<string>();
  //   const parentIds = new Set<string>();

  //   console.log('Matched indices:', indices);

  //   // Collect matched category IDs
  //   for (const idx of indices) {
  //     const category = categories[idx];
  //     if (category) {
  //       matchedIds.add(category.id);
  //       console.log('Matched category:', category.id, category.name);

  //       // Add all parent IDs
  //       const parts = category.id.split('.');
  //       for (let i = 1; i < parts.length; i++) {
  //         const parentId = parts.slice(0, i).join('.');
  //         parentIds.add(parentId);
  //         console.log('  Adding parent:', parentId);
  //       }
  //     }
  //   }

  //   console.log('Parent IDs with matches:', Array.from(parentIds));
  //   this.matchedCategoryIds.set(matchedIds);
  //   this.parentIdsWithMatches.set(parentIds);
  // }

  // Check if a category has matching descendants
  protected hasMatchingDescendants(category: ProductCategory): boolean {
    const result = this.parentIdsWithMatches().has(category.id);
    return result;
  }
  protected isMatched(category: ProductCategory): boolean {
    const result = this.matchedCategories().some((c) => c.id === category.id);
    return result;
  }

  // ControlValueAccessor callbacks - stored for later delegation
  private _storedOnChange: ((value: ProductCategory | string | null) => void) | null = null;
  private _storedOnTouched: (() => void) | null = null;
  private _storedValue: ProductCategory | string | null = null;
  private _storedDisabled: boolean | null = null;

  constructor() {
    // When categories load, resolve any stored string value (category ID) to the actual category object
    effect(() => {
      const categories = this.choices();
      const storedValue = this._storedValue;

      // If we have a string value (category ID) and categories are loaded
      if (typeof storedValue === 'string' && categories.length > 0) {
        const matchingCategory = categories.find((cat) => cat.id === storedValue);

        if (matchingCategory) {
          // Found the category, update combobox with the actual object
          const combobox = this.comboboxRef();
          if (combobox) {
            // Use writeValue to ensure proper initialization
            this.writeValue(storedValue);
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Register wrapped callbacks with the child combobox
    const combobox = this.comboboxRef();

    // Wrap onChange to transform object values to strings for form compatibility
    combobox.registerOnChange((value: ProductCategory | string | null) => {
      if (this._storedOnChange) {
        // If value is an object (ProductCategory), extract the ID for the form
        // If it's a string or null, pass it through as-is
        const formValue = value && typeof value === 'object' ? value.id : value;
        this._storedOnChange(formValue);
      }
    });

    // Pass through onTouched
    if (this._storedOnTouched) {
      combobox.registerOnTouched(this._storedOnTouched);
    }

    // Set initial value if stored (using writeValue which handles ID resolution)
    const formCategoryValue = this._formGroup?.form.get('category')?.value;
    if (this._storedValue !== null) {
      this.writeValue(this._storedValue);
    } else {
      // Safety net: if _storedValue wasn't captured but form has a string value, apply it directly
      if (typeof formCategoryValue === 'string') {
        const matchingCategory = this.choices().find((cat) => cat.id === formCategoryValue);
        if (!matchingCategory) {
          combobox.writeValue(formCategoryValue);
        }
      }
    }

    // Set disabled state if stored
    if (this._storedDisabled !== null) {
      combobox.setDisabledState(this._storedDisabled);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: ProductCategory | string | null): void {
    this._storedValue = value;
    const combobox = this.comboboxRef();

    if (combobox) {
      // If value is a string (category ID), try to resolve it to a category object
      if (typeof value === 'string') {
        const categories = this.choices();
        const matchingCategory = categories.find((cat) => cat.id === value);

        if (matchingCategory) {
          // Found the category, pass the object
          combobox.writeValue(matchingCategory);
        } else {
          // No exact match — pass string through as typed text (e.g. partial startsWith restore)
          combobox.writeValue(value);
        }
      } else {
        // Value is null or already a category object, pass through
        combobox.writeValue(value);
      }
    }
  }

  registerOnChange(fn: (value: ProductCategory | string | null) => void): void {
    this._storedOnChange = fn;
    // Don't delegate immediately - will be set up in ngAfterViewInit
  }

  registerOnTouched(fn: () => void): void {
    this._storedOnTouched = fn;
    // Don't delegate immediately - will be set up in ngAfterViewInit
  }

  setDisabledState?(isDisabled: boolean): void {
    this._storedDisabled = isDisabled;
    const combobox = this.comboboxRef();
    if (combobox) {
      combobox.setDisabledState(isDisabled);
    }
  }

  getCategoryPath(category: ProductCategory): ProductCategory[] {
    const categories = this.choices();
    const path: ProductCategory[] = [];
    let current: ProductCategory | null = category;

    while (current) {
      path.unshift(current);
      const idParts = current.id.split('.') as string[];
      idParts.pop();
      const parentId = idParts.join('.');
      current = categories.find((c) => c.id === parentId) || null;
    }
    return path;
  }

  getParentCategoryPath(path: ProductCategory[]): ProductCategory[] {
    if (path.length > 1) {
      return path.slice(0, -1);
    }
    return [];
  }

  getChildCategorySelected(path: ProductCategory): boolean {
    const selected = this.selectedItem();
    const selectedId = selected?.id;
    if (!selectedId) {
      return false;
    }

    const parent = `${path.id}.`;
    return selectedId.startsWith(parent);
  }
}
